// File: apps/app-web/src/hooks/useAddLateFee.ts
// Purpose: Hook for manually adding late fees to subscription enrollments

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentCompany } from './useCurrentCompany';
import { supabase } from '../lib/supabase';

export interface AddLateFeeParams {
  enrollmentId: string;
  amount: number;
  daysLate?: number;
  notes?: string;
}

export interface AddLateFeeResponse {
  success: boolean;
  late_fee_id?: string;
  enrollment_id?: string;
  message?: string;
  error?: string;
}

export function useAddLateFee() {
  const queryClient = useQueryClient();
  const { data: currentCompanyId } = useCurrentCompany();

  return useMutation({
    mutationFn: async (params: AddLateFeeParams): Promise<AddLateFeeResponse> => {
      const { enrollmentId, amount, daysLate } = params;

      if (!currentCompanyId) {
        throw new Error('Company ID is required');
      }

      // Get enrollment details to find user_id and billing_schedule_id
      const { data: enrollment, error: enrollmentError } = await supabase
        .from('subscription_enrollments')
        .select('id, user_id, company_id')
        .eq('id', enrollmentId)
        .single();

      if (enrollmentError || !enrollment) {
        throw new Error('Enrollment not found');
      }

      // Get the most recent billing schedule for this enrollment
      const { data: billingSchedule } = await supabase
        .from('billing_schedules')
        .select('id')
        .eq('enrollment_id', enrollmentId)
        .order('scheduled_billing_date', { ascending: false })
        .limit(1)
        .single();

      // Insert late fee record
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: lateFee, error: feeError } = await (supabase.from as any)('late_fees')
        .insert({
          enrollment_id: enrollmentId,
          user_id: enrollment.user_id,
          company_id: currentCompanyId,
          amount,
          stage: 1, // Manual late fees are always stage 1
          days_late: daysLate || 0,
          billing_schedule_id: billingSchedule?.id || null,
          applied_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (feeError) {
        throw new Error(feeError.message || 'Failed to add late fee');
      }

      return {
        success: true,
        late_fee_id: (lateFee as unknown as { id: string })?.id,
        enrollment_id: enrollmentId,
        message: 'Late fee added successfully',
      };
    },
    onSuccess: () => {
      // Invalidate subscription-related queries
      queryClient.invalidateQueries({ queryKey: ['subscriptionsWithAmountDue'] });
      queryClient.invalidateQueries({ queryKey: ['local-subscription-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['user-subscriptions-with-discounts'] });
      queryClient.invalidateQueries({ queryKey: ['child-subscriptions-with-discounts'] });
      queryClient.invalidateQueries({ queryKey: ['billing-account-subscriptions-with-discounts'] });
    },
  });
}
